import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import get from "lodash/get";
import { showPopupFlag } from "lib/actions/userInterface";
import handlePopupFlagError from "lib/errors/handlePopupFlagError";
import { useDispatch, useSelector } from "store";
import { selectKeycards, updateKeycard } from "store/reducers";
import { Pagination, TKeycard } from "types";
import StatusPill from "components/StatusPill";
import { TableLink, SmartTable, SmartTablePagination } from "components/Table";
import { Text } from "components/Typography";
import { KeycardTableRowDropdown } from "components/shared/features/configurations/keycards/KeycardTableRowDropdown/KeycardTableRowDropdown";

type KeycardsTableProps = {
  loading: boolean;
  keycardIds: string[];
  paging: Pagination;
  onFetch: (pageStart: number, pageLimit: number) => void;
};

type RowData = {
  row: {
    original: TKeycard;
  };
};

export const KeycardsTable = ({ loading, keycardIds, paging, onFetch }: KeycardsTableProps) => {
  const { pathname: url } = useLocation();
  const dispatch = useDispatch();
  const keycards = useSelector(selectKeycards);

  const saveChanges = async (keycard: TKeycard) => {
    try {
      await dispatch(updateKeycard(keycard)).unwrap();
      dispatch(
        showPopupFlag({
          appearance: "success",
          title: "Keycard updated",
        }),
      );
    } catch (err) {
      handlePopupFlagError(err);
    }
  };

  const rowData = useMemo(() => {
    return keycardIds.map((id) => keycards[id]).filter(Boolean);
  }, [keycardIds, keycards]);
  const columns = useMemo(() => {
    return [
      {
        Header: "ID",
        Cell: ({ row }: RowData) => {
          const { id } = row.original;
          return (
            <TableLink to={`${url}/${id}`}>
              <Text value={id} />
            </TableLink>
          );
        },
      },
      {
        Header: "Name",
        Cell: ({ row }: RowData) => {
          return (
            <Text
              canEdit={true}
              placeholder="Enter keycard name"
              value={get(row, "original.name", "")}
              onConfirm={(value) => saveChanges({ ...row.original, name: value })}
            />
          );
        },
      },
      {
        Header: "Description",
        Cell: ({ row }: RowData) => {
          return (
            <Text
              canEdit={true}
              placeholder="Keycard description"
              value={get(row, "original.description", "")}
              onConfirm={(value) =>
                saveChanges({
                  ...row.original,
                  description: value,
                })
              }
            />
          );
        },
      },
      {
        Header: "Status",
        Cell: ({ row }: RowData) => {
          const { revoked } = row.original;
          return revoked ? <StatusPill text="Revoked" color="danger" /> : <StatusPill text="Active" color="success" />;
        },
      },
      {
        id: "actions",
        Cell: ({ row }: RowData) => {
          const { id } = row.original;
          return <KeycardTableRowDropdown keycardId={id} onDelete={() => onFetch(0, paging.pageLimit)} />;
        },
      },
    ];
  }, []);

  return (
    <>
      <SmartTable loading={loading} columns={columns} data={rowData} />
      <SmartTablePagination paging={paging} onChange={onFetch} />
    </>
  );
};
